<script>
import ManageAllocationModalFormHeader from '../ManageAllocationModalFormHeader/ManageAllocationModalFormHeader.vue';
import ManageAllocationFormAccept from '../ManageAllocationFormAccept/ManageAllocationFormAccept.vue';
import {isReleased, isSoldOut} from '../../services/DateToolService';
import {
    validateUpdateAllocationPossible,
    validateUpdateUnits,
    validateUpdateReleaseDate,
    validateUpdateReleaseDays,
} from "../../services/ValidatorService";
import {mapState, mapActions, mapGetters} from 'vuex';

export default {
    components: {
        ManageAllocationModalFormHeader,
        ManageAllocationFormAccept
    },
    data() {
        return {
            errors: "",
            updateMessage: "update",
            edit: false,
            editField: undefined,
            showInstruction: false
        }
    },
    computed: {
        ...mapState({
            trustedSupplier: state => state.auth.supplier.trusted,
            allocation: state => state.allocation.modal.overlay.allocation.item,
            original: state => state.allocation.modal.overlay.allocation.original,
            error: state => state.allocation.modal.overlay.error,
            selected: state => state.allocation.form.filters.selected,
            items: state => state.allocation.allocations.items,
            loading: state => state.allocation.modal.overlay.loading,
            from: state => state.allocation.calendar.from,
            to: state => state.allocation.calendar.to,
            user: state => state.auth.user
        }),
        ...mapGetters({
            changed: 'allocation/overlayAllocationChanged',
            changeRequestForAllocation: 'allocation/changeRequestForAllocation',
            getAllocationTypes: 'allocation/getAllocationTypes',
            getStatisticsForPeriod: 'allocation/getStatisticsForPeriod',
            getAllocationReleaseTypes: 'allocation/getAllocationReleaseTypes'
        }),
        canUpdate() {
            return !this.changeRequestForAllocation.length
                && validateUpdateAllocationPossible(this.original, this.allocation, this.trustedSupplier)
                || this.user.contractManager;
        },
        canBlock() {
            return !this.changeRequestForAllocation.length
                && ((this.checkIfReleased && this.original.releaseType !== 'BLOCKED')
                || this.original.type === 'REQUEST'
                || this.original.type === 'FREESALE'
                || this.user.contractManager
                || this.trustedSupplier)
        },
        canRequestBlock() {
            return !this.changeRequestForAllocation.length
                && !this.trustedSupplier
                && this.original.type !== 'BLOCKED'
                && !this.checkIfReleased
        },
        checkIfReleased() {
            return isReleased(this.original.to, this.original) || isSoldOut(this.from, this.to, this.original);
        }
    },
    methods: {
        ...mapActions({
            setOverlayRow: 'allocation/setOverlayRow',
            validateInputs: 'allocation/validateUpdateAllocationInputs',
            submit: 'allocation/submitForm',
            resetForm: 'allocation/resetForm',
            updateAllocation: 'allocation/updateAllocation',
            requestUpdateAllocation: 'allocation/requestUpdateAllocation',
            blockAllocation: 'allocation/blockAllocation',
            requestBlockAllocation: 'allocation/requestBlockAllocation',
            resetOverlayRow: 'allocation/resetOverlayRow'
        }),
        openEdit(field) {
            if (field === 'units' && this.allocation.type !== 'ALLOTMENT') {
                return;
            }
            this.edit = true;
            this.editField = field;
        },
        closeEdit() {
            this.edit = false;
            this.editField = undefined;
            this.resetOverlayRow();
        },
        update(obj) {
            this.$v.$touch();
            this.setOverlayRow(obj);
        },
        process(type) {
            this.updateMessage = type;
            this.$v.$touch();
            this.submit([{from: this.original.from.format('DD/MM/YYYY'), to: this.original.to.format('DD/MM/YYYY')}]);
        },
        accept() {
            switch(this.updateMessage) {
            case 'submit':
                this.updateAllocation();
                break;
            case 'request':
                this.requestUpdateAllocation();
                break;
            case 'block':
                this.setOverlayRow({type: "BLOCKED"});
                this.blockAllocation(this.allocation);
                break;
            case 'requestBlock':
                this.setOverlayRow({type: "BLOCKED"});
                this.requestBlockAllocation(this.allocation);
                break;
            }
        }
    },
    validations() {
        return {
            allocation: {
                units: {
                    ValidateUnits: (units) => {
                        let valid = validateUpdateUnits(units, this.allocation);
                        if (!valid.valid) {
                            this.errors = valid.message;
                        }
                        return valid.valid;
                    },
                },
                releaseDays: {
                    validateReleaseDays: (days) => {
                        if (this.original.releaseMethod === 'days') {
                            let valid = validateUpdateReleaseDays(days);
                            if (!valid.valid) {
                                this.errors = valid.message;
                            }
                            return valid.valid;
                        }
                        return true;
                    }
                },
                releaseDate: {
                    validateReleaseDate: (date) => {
                        if (this.original.releaseMethod === 'date') {
                            let valid = validateUpdateReleaseDate(date, this.original);
                            if (!valid.valid) {
                                this.errors = valid.message;
                            }
                            return valid.valid;
                        }
                        return true;
                    }
                }
            }
        }
    }
}
</script>

<template>
    <div class="manage-allocation-modal-overlay-form">
        <div id="container" class="manage-allocation-modal-overlay-form__container">
            <div class="manage-allocation-modal-overlay-form__content">
                <manage-allocation-form-accept
                    :label="$t('manageAllocationModalOverlayForm.accept.' + updateMessage)"
                    :method="updateMessage"
                    @accept="accept"/>
                <div v-if="$v.$error"
                     class="manage-allocation-modal-overlay-form__alert manage-allocation-modal-overlay-form__alert--danger">
                    {{ $t('manageAllocationModalOverlayForm.validator.' + errors) }}
                </div>
                <div v-if="!changeRequestForAllocation.length && changed && !canUpdate"
                     class="manage-allocation-modal-overlay-form__alert manage-allocation-modal-overlay-form__alert--warning">
                    {{ $t('manageAllocationModalOverlayForm.request') }}
                </div>
                <div v-if="changeRequestForAllocation.length"
                     class="manage-allocation-modal-overlay-form__alert manage-allocation-modal-overlay-form__alert--warning">
                    {{ $t('manageAllocationModalOverlayForm.requestPending') }}
                </div>
                <div v-if="checkIfReleased"
                     class="manage-allocation-modal-overlay-form__alert manage-allocation-modal-overlay-form__alert--warning">
                    {{ $t('manageAllocationModalOverlayForm.released') }}
                </div>
                <div class="manage-allocation-modal-overlay-form__instruction-toggle"
                     @click="showInstruction = !showInstruction">
                    {{ $t('manageAllocationModalOverlayForm.instruction') }}
                </div>
                <div v-if="showInstruction" class="manage-allocation-modal-overlay-form__instruction">
                    <div class="manage-allocation-modal-overlay-form__instruction-text">
                        <div class="manage-allocation-modal-overlay-form__text-default">
                            {{ $t('manageAllocationModalOverlayForm.editable.information.default') }}
                        </div>
                        <div v-if="!user.contractManager && trustedSupplier"
                             v-html="$t('manageAllocationModalOverlayForm.instructionTrusted')"/>
                        <div v-if="!user.contractManager && !trustedSupplier"
                             v-html="$t('manageAllocationModalOverlayForm.instructionNotTrusted')"/>
                    </div>
                </div>
                <div v-if="edit" class="manage-allocation-modal-overlay-form__edit">
                    <div v-if="editField === 'type'" class="manage-allocation-modal-overlay-form__edit-item">
                        <div class="manage-allocation-modal-overlay-form__edit-label">
                            {{ $t('manageAllocationModalOverlayForm.editable.type') }}
                        </div>
                        <select :value="allocation.type" class="manage-allocation-modal-overlay-form__select"
                                @change="update({type: $event.target.value })">
                            <option v-for="(type, index) in getAllocationTypes"
                                    :value="type"
                                    :key="'allocation-type__' + index"
                                    class="manage-allocation-modal-overlay-form__option">
                                {{ $t('manageAllocationModal.form.type.' + type.toLowerCase()) }}
                            </option>
                        </select>
                    </div>
                    <div v-if="editField === 'units'" class="manage-allocation-modal-overlay-form__edit-item">
                        <div class="manage-allocation-modal-overlay-form__edit-label">
                            {{ $t('manageAllocationModalOverlayForm.editable.units') }}
                        </div>
                        <input v-number-only
                               :value="allocation.units"
                               class="manage-allocation-modal-overlay-form__edit-input"
                               @input="update({units: parseInt($event.target.value)})">
                        <button :disabled="allocation.type !== 'ALLOTMENT' || allocation.units <= 1"
                                class="manage-allocation-modal-overlay-form__increment-btn"
                                @click="update({units: parseInt(allocation.units) - 1})">
                            -
                        </button>
                        <button :disabled="allocation.type !== 'ALLOTMENT'"
                                class="manage-allocation-modal-overlay-form__decrement-btn"
                                @click="update({units: parseInt(allocation.units) + 1})">
                            +
                        </button>
                    </div>
                    <div v-if="editField === 'releaseType'" class="manage-allocation-modal-overlay-form__edit-item">
                        <div class="manage-allocation-modal-overlay-form__edit-label">
                            {{ $t('manageAllocationModalOverlayForm.editable.releaseType') }}
                        </div>
                        <select :value="allocation.releaseType" class="manage-allocation-modal-overlay-form__select"
                                @change="update({releaseType: $event.target.value })">
                            <option v-for="(releaseType, index) in getAllocationReleaseTypes"
                                    :value="releaseType"
                                    :key="'allocation-release-type__' + index"
                                    class="manage-allocation-modal-overlay-form__option">
                                {{ $t('manageAllocationModal.form.type.' + releaseType.toLowerCase()) }}
                            </option>
                        </select>
                    </div>
                    <div v-if="editField === 'releaseDays'" class="manage-allocation-modal-overlay-form__edit-item">
                        <div class="manage-allocation-modal-overlay-form__edit-label">
                            {{ $t('manageAllocationModalOverlayForm.editable.releaseDays') }}
                        </div>
                        <input v-number-only
                               :value="allocation.releaseDays"
                               :placeholder="$t('manageAllocationFormRow.days')"
                               class="manage-allocation-modal-overlay-form__edit-input"
                               @input="update({releaseDays: $event.target.value, releaseDate: null})">
                        <button :disabled="allocation.releaseDays <= 1"
                                class="manage-allocation-modal-overlay-form__increment-btn"
                                @click="update({releaseDays: parseInt(allocation.releaseDays) - 1})">
                            -
                        </button>
                        <button class="manage-allocation-modal-overlay-form__decrement-btn"
                                @click="update({releaseDays: parseInt(allocation.releaseDays) + 1})">
                            +
                        </button>
                    </div>
                    <div v-if="editField === 'releaseDate'" class="manage-allocation-modal-overlay-form__edit-item">
                        <div class="manage-allocation-modal-overlay-form__edit-label">
                            {{ $t('manageAllocationModalOverlayForm.editable.releaseDate') }}
                        </div>
                        <input v-mask="'##/##/####'"
                               :placeholder="$t('manageAllocationFormRow.dateFormat')"
                               :value="allocation.releaseDate"
                               type="text"
                               class="manage-allocation-modal-overlay-form__date"
                               @input="update({releaseDate: $event.target.value, releaseDays: null})">
                    </div>
                </div>
                <div class="manage-allocation-modal-overlay-form__form">
                    <div class="manage-allocation-modal-overlay-form__form-item">
                        <span class="manage-allocation-modal-overlay-form__item-label">
                            {{ $t('manageAllocationModalOverlayForm.labels.period') }}:
                        </span>
                        <span class="manage-allocation-modal-overlay-form__item-value">
                            {{ original.from | moment("DD/MM/YYYY") }} - {{ original.to | moment("DD/MM/YYYY") }}
                        </span>
                    </div>
                    <div class="manage-allocation-modal-overlay-form__form-item--editable"
                         @click="openEdit('type')">
                        <span class="manage-allocation-modal-overlay-form__item-label">
                            {{ $t('manageAllocationModalOverlayForm.labels.type') }}:
                        </span>
                        <span class="manage-allocation-modal-overlay-form__item-value">
                            {{ $t('manageAllocationModal.form.type.' +
                            (original.type ? original.type.toLowerCase() : 'null')) }}
                        </span>
                        <span v-if="original.type !== allocation.type"
                              class="manage-allocation-modal-overlay-form__item-change">
                            {{ $t('manageAllocationModal.form.type.' +
                            (allocation.type ? allocation.type.toLowerCase() : 'null')) }}
                        </span>
                    </div>
                    <div :class="{'manage-allocation-modal-overlay-form__form-item--null' : allocation.units === null}"
                         class="manage-allocation-modal-overlay-form__form-item--editable"
                         @click="openEdit('units')">
                        <span class="manage-allocation-modal-overlay-form__item-label">
                            {{ $t('manageAllocationModalOverlayForm.labels.units') }}:
                        </span>
                        <span class="manage-allocation-modal-overlay-form__item-value">
                            {{ original.units }}
                        </span>
                        <span v-if="original.units !== allocation.units"
                              class="manage-allocation-modal-overlay-form__item-change">
                            {{ allocation.units }}
                        </span>
                    </div>
                    <div class="manage-allocation-modal-overlay-form__form-item--editable"
                         @click="openEdit('releaseType')">
                        <span class="manage-allocation-modal-overlay-form__item-label">
                            {{ $t('manageAllocationModalOverlayForm.labels.releaseMethod') }}:
                        </span>
                        <span class="manage-allocation-modal-overlay-form__item-value">
                            {{ $t('manageAllocationModal.form.type.' +
                            (original.releaseType ? original.releaseType.toLowerCase() : 'null')) }}
                        </span>
                        <span v-if="original.releaseType !== allocation.releaseType"
                              class="manage-allocation-modal-overlay-form__item-change">
                            {{ $t('manageAllocationModal.form.type.' +
                            (allocation.releaseType ? allocation.releaseType.toLowerCase() : 'null')) }}
                        </span>
                    </div>
                    <div v-if="original.releaseDate !== null"
                         class="manage-allocation-modal-overlay-form__form-item--editable"
                         @click="openEdit('releaseDate')">
                        <span class="manage-allocation-modal-overlay-form__item-label">
                            {{ $t('manageAllocationModalOverlayForm.labels.releaseDate') }}:
                        </span>
                        <span class="manage-allocation-modal-overlay-form__item-value">
                            {{ original.releaseDate }}
                        </span>
                        <span v-if="original.releaseDate !== allocation.releaseDate"
                              class="manage-allocation-modal-overlay-form__item-change">
                            {{ allocation.releaseDate }}
                        </span>
                    </div>
                    <div v-if="original.releaseDays !== null"
                         class="manage-allocation-modal-overlay-form__form-item--editable"
                         @click="openEdit('releaseDays')">
                        <span class="manage-allocation-modal-overlay-form__item-label">
                            {{ $t('manageAllocationModalOverlayForm.labels.releaseDays') }}:
                        </span>
                        <span class="manage-allocation-modal-overlay-form__item-value">
                            {{ original.releaseDays }}
                        </span>
                        <span v-if="original.releaseDays !== allocation.releaseDays"
                              class="manage-allocation-modal-overlay-form__item-change">
                            {{ allocation.releaseDays }}
                        </span>
                    </div>
                    <div v-if="canBlock"
                         class="manage-allocation-modal-overlay-form__block"
                         @click="process('block')">
                        {{ $t('manageAllocationModalOverlayForm.accept.block') }}
                    </div>
                    <div v-if="!canBlock && canRequestBlock"
                         class="manage-allocation-modal-overlay-form__block"
                         @click="process('requestBlock')">
                        {{ $t('manageAllocationModalOverlayForm.accept.requestBlock') }}
                    </div>
                    <div class="manage-allocation-modal-overlay-form__edit-buttons">
                        <button v-if="canUpdate"
                                :disabled="!changed || this.$v.$error || loading"
                                class="manage-allocation-modal-overlay-form__button-update"
                                @click="process('submit')">
                            {{ $t('manageAllocationModalOverlayForm.buttons.update') }}
                        </button>
                        <button v-if="!canUpdate"
                                :disabled="changeRequestForAllocation.length || !changed || this.$v.$error || loading"
                                class="manage-allocation-modal-overlay-form__button-request"
                                @click="process('request')">
                            {{ $t('manageAllocationModalOverlayForm.buttons.request') }}
                        </button>
                        <button class="manage-allocation-modal-overlay-form__button-reset" @click="closeEdit">
                            {{ $t('manageAllocationModalOverlayForm.buttons.reset') }}
                        </button>
                    </div>
                </div>
                <div class="manage-allocation-modal-overlay-form__statistics">
                    <div class="manage-allocation-modal-overlay-form__statistics-item">
                        <span class="manage-allocation-modal-overlay-form__item-label">
                            {{ $t('manageAllocationModalOverlayForm.labels.bookings') }}:
                        </span>
                        <span class="manage-allocation-modal-overlay-form__item-value">
                            <i v-if="!getStatisticsForPeriod.bookings"
                               class="manage-allocation-modal-overlay-form__icon-minus fas fa-minus-square"/>
                            <i v-if="getStatisticsForPeriod.bookings"
                               class="manage-allocation-modal-overlay-form__icon-plus fas fa-check-square"/>
                        </span>
                    </div>
                    <div v-if="getStatisticsForPeriod.bookings"
                         :title="$t('manageAllocationModalOverlayForm.labels.bookingsMaxCountToolTip')"
                         class="manage-allocation-modal-overlay-form__list-item">
                        <span class="manage-allocation-modal-overlay-form__list-label">
                            {{ $t('manageAllocationModalOverlayForm.labels.bookingsMaxCount') }}:
                        </span>
                        <span class="manage-allocation-modal-overlay-form__list-value">
                            {{ getStatisticsForPeriod.bookingsMaxCount }}
                        </span>
                    </div>
                    <div class="manage-allocation-modal-overlay-form__statistics-item">
                        <div class="manage-allocation-modal-overlay-form__item-label">
                            {{ $t('manageAllocationModalOverlayForm.labels.arrivalRules') }}:
                        </div>
                        <div v-if="getStatisticsForPeriod.arrivalRules.length === 0"
                             class="manage-allocation-modal-overlay-form__item-alert">
                            <span>
                                <i class="fas fa-exclamation-triangle"/>
                            </span>
                            <span>
                                {{ $t('manageAllocationModalOverlayForm.labels.noArrivalRules') }}
                            </span>
                        </div>
                        <div v-for="(rule, index) in getStatisticsForPeriod.arrivalRules"
                             :key="'arrivalRule_' + index"
                             class="manage-allocation-modal-overlay-form__item-list">
                            <div class="manage-allocation-modal-overlay-form__list-item">
                                <span class="manage-allocation-modal-overlay-form__list-label">
                                    {{ $t('manageAllocationModalOverlayForm.labels.period') }}:
                                </span>
                                <span class="manage-allocation-modal-overlay-form__list-value">
                                    {{ rule.startDate | moment("DD/MM/YYYY") }} - {{ rule.endDate | moment("DD/MM/YYYY") }}
                                </span>
                            </div>
                            <div class="manage-allocation-modal-overlay-form__list-item">
                                <span class="manage-allocation-modal-overlay-form__list-label">
                                    {{ $t('manageAllocationModalOverlayForm.labels.arrivalTypes.label') }}:
                                </span>
                                <span class="manage-allocation-modal-overlay-form__list-value">
                                    {{ $t('manageAllocationModalOverlayForm.labels.arrivalTypes.' + rule.type) }}
                                </span>
                            </div>
                            <div class="manage-allocation-modal-overlay-form__list-item">
                                <span class="manage-allocation-modal-overlay-form__list-label">
                                    {{ $t('manageAllocationModalOverlayForm.labels.arrivalRuleStayLength') }}:
                                </span>
                                <span class="manage-allocation-modal-overlay-form__list-value">
                                    {{ rule.minRental }} - {{ rule.maxRental }}
                                </span>
                            </div>
                            <div class="manage-allocation-modal-overlay-form__item-days">
                                <span class="manage-allocation-modal-overlay-form__item-day">
                                    <div class="manage-allocation-modal-overlay-form__list-label">{{ $t('contract.form.arrivalRules.weekdays.sunday') }}</div>
                                    <div :class="['manage-allocation-modal-overlay-form__checkbox', {'manage-allocation-modal-overlay-form__checkbox--checked': rule.sunday}]"/>
                                </span>
                                <span class="manage-allocation-modal-overlay-form__item-day">
                                    <div class="manage-allocation-modal-overlay-form__list-label">{{ $t('contract.form.arrivalRules.weekdays.monday') }}</div>
                                    <div :class="['manage-allocation-modal-overlay-form__checkbox', {'manage-allocation-modal-overlay-form__checkbox--checked': rule.monday}]"/>
                                </span>
                                <span class="manage-allocation-modal-overlay-form__item-day">
                                    <div class="manage-allocation-modal-overlay-form__list-label">{{ $t('contract.form.arrivalRules.weekdays.tuesday') }}</div>
                                    <div :class="['manage-allocation-modal-overlay-form__checkbox', {'manage-allocation-modal-overlay-form__checkbox--checked': rule.tuesday}]"/>
                                </span>
                                <span class="manage-allocation-modal-overlay-form__item-day">
                                    <div class="manage-allocation-modal-overlay-form__list-label">{{ $t('contract.form.arrivalRules.weekdays.wednesday') }}</div>
                                    <div :class="['manage-allocation-modal-overlay-form__checkbox', {'manage-allocation-modal-overlay-form__checkbox--checked': rule.wednesday}]"/>
                                </span>
                                <span class="manage-allocation-modal-overlay-form__item-day">
                                    <div class="manage-allocation-modal-overlay-form__list-label">{{ $t('contract.form.arrivalRules.weekdays.thursday') }}</div>
                                    <div :class="['manage-allocation-modal-overlay-form__checkbox', {'manage-allocation-modal-overlay-form__checkbox--checked': rule.thursday}]"/>
                                </span>
                                <span class="manage-allocation-modal-overlay-form__item-day">
                                    <div class="manage-allocation-modal-overlay-form__list-label">{{ $t('contract.form.arrivalRules.weekdays.friday') }}</div>
                                    <div :class="['manage-allocation-modal-overlay-form__checkbox', {'manage-allocation-modal-overlay-form__checkbox--checked': rule.friday}]"/>
                                </span>
                                <span class="manage-allocation-modal-overlay-form__item-day">
                                    <div class="manage-allocation-modal-overlay-form__list-label">{{ $t('contract.form.arrivalRules.weekdays.saturday') }}</div>
                                    <div :class="['manage-allocation-modal-overlay-form__checkbox', {'manage-allocation-modal-overlay-form__checkbox--checked': rule.saturday}]"/>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>



// WEBPACK FOOTER //
// src/components/ManageAllocationModalOverlayForm/ManageAllocationModalOverlayForm.vue